import { send } from "@utils/fetch";

import getStore from "src/utils/redux/store";
import { getSearchQuery, getURLParams } from "../urlParams";
import {
  isAppFreeOnboardingRoute,
  isEU,
  isMobile,
  isHealthRoute,
} from "../userSegment";
import { prepareGrowthAPIParameters } from "../services/api-params";
import { captureException } from "src/utils/error";
import { trackEvent, trackMmpEvent } from "./tracker";
import { errorConstants } from "../services/PurchaseErrorMessages";
import { getCountryCode, getLanguage, getRouteId } from "../meristemContext";
import { getECTExperiments } from "../experiment";
import { NoomError } from "../error/NoomError";
import { Opaque } from "type-fest";
import { updateSessionState } from "src/pageDefinitions/session";
import { updateUserData } from "@utils/redux/slices/userData";
import { getWeightLossGoalData } from "../pace";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { isNursing } from "../pace/planTimeAdjustmentNursing";
import { genderConstantsMapping } from "../sexAndGender";
import { getInAppAutoCookie } from "../authCookieParser";
import { publishBuyflowSurveyAnswersInUserModel } from "./userModel";
import { attachPhotoToUser } from "./profileImageUpload";

const API_ENDPOINT_V1 = "/api/enrollment/v1/enroll/";
const API_ENDPOINT_V1_FREE = "/api/enrollment/v1/enrollInFreeProgram/";
const API_ENDPOINT_V1_FREE_ON_WEB =
  "/api/enrollment/v1/enrollInFreeProgramOnWeb/";

type DeepLinkString = Opaque<string>;

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    claimedByEmail?: string;
    enrollmentResult?: string;
    appDeepLink?: DeepLinkString;
  }
}

interface BaseEnrollmentData {
  name: string;
  email: string;
  password: string;
  gender: string;
  age: number;
  heightInCm: number;
  weightInKg: number;
  language: string;
  isNursing?: boolean;
}

export interface EnrollmentData extends BaseEnrollmentData {
  phone_number: string;
  upid: string;
  activityLevel: string;
  piv?: boolean;
}

export interface CollapsedEnrollmentData extends BaseEnrollmentData {
  phoneNumber: string;
}

export interface EnrollmentParams {
  action: string;
  enrollmentData?: EnrollmentData;
  piv?: boolean;
  phone_number: string;
  email: string;

  curriculum?: string;
}

export interface CollapsedEnrollmentParams {
  userProfileData: CollapsedEnrollmentData;
  curriculum?: string;
}

export interface EnrollmentResult {
  result: string;
  claimed_by_email?: string;
}

export class EnrollmentError extends NoomError {
  constructor(public code = "Unknown") {
    super(`Enrollment Error: ${code}`);
  }
}

function getReduxProperties() {
  const state = getStore().getState();
  const { userData, serverContext } = state;
  return {
    is_eu_citizen: isEU(),
    gdpr_consent: serverContext.gdpr_consent,
    country_code: getCountryCode(),
    language: getLanguage(),
    user_id: userData.user_id,
    experiments: getECTExperiments(),
    device_is_mobile: isMobile(),
  };
}

function prepareEnrollmentRequest(params) {
  const payload = {
    ...params,
    ...getReduxProperties(),
  };
  return payload;
}

function getEmailSources() {
  const state = getStore().getState();
  const { userData, serverContext, paymentEnrollmentForm } = state;
  const { email: inAppEmail } = getInAppAutoCookie() ?? {};

  return [
    inAppEmail,
    ...(isHealthRoute() ? [paymentEnrollmentForm.enrollmentInfo.email] : []),
    userData.email,
    serverContext.email,
  ];
}

function getNameSources(
  welcomeName: string | undefined,
  firstName: string | undefined
) {
  return [welcomeName, ...(isHealthRoute() ? [firstName] : [])];
}

function resolveFirstAvailable<T>(
  sources: Array<T | undefined>
): T | undefined {
  return sources.find(Boolean);
}

function prepareFreeUserProfileData(surveyAnswers: SurveyAnswersState) {
  const {
    gender,
    heightCm,
    weightKg,
    ageRange,
    welcomeName,
    firstName,
    liabilityWaiver,
  } = surveyAnswers;

  const payload = {
    email: resolveFirstAvailable(getEmailSources()),
    name: resolveFirstAvailable(getNameSources(welcomeName, firstName)),
    age: parseInt(ageRange[0], 10),
    language: getLanguage(),
    heightInCm: heightCm,
    weightInKg: weightKg,
    isNursing: isNursing(surveyAnswers),
    gender: genderConstantsMapping[gender[0]],
    ...(liabilityWaiver &&
      liabilityWaiver[0] === "agree" && {
        liabilityWaiverConsent: liabilityWaiver[0].toUpperCase(),
      }),
  };

  return payload;
}

export function enrollUser(
  params: EnrollmentParams
): Promise<EnrollmentResult> {
  const payload = prepareEnrollmentRequest(params);
  const urlParams = new URLSearchParams(getURLParams());
  prepareGrowthAPIParameters(urlParams);

  return send(
    "POST",
    `${API_ENDPOINT_V1}${getSearchQuery(urlParams)}`,
    payload
  );
}

export async function requestEnrollment(params: CollapsedEnrollmentParams) {
  const store = getStore();
  // Attempt to enroll the user.
  let enrollmentResult: string;
  let claimedByEmail: string;
  let deeplinkUrl: string;
  let accessCode: string;
  let success = false;

  try {
    const payload = prepareEnrollmentRequest({
      action: "create_account",

      email: params.userProfileData.email,
      name: params.userProfileData.name,
      password: params.userProfileData.password,
      phone_number: params.userProfileData.phoneNumber,

      ...params,
    });
    const urlParams = new URLSearchParams(getURLParams());
    prepareGrowthAPIParameters(urlParams);

    const json = await send(
      "POST",
      `/api/enrollment/v2/create_account/${getSearchQuery(urlParams)}`,
      payload
    );
    ({
      result: enrollmentResult,
      claimed_by_email: claimedByEmail,
      deeplink_url: deeplinkUrl,
      access_code: accessCode,
    } = json);
    const successResults = [
      "account_created",
      "free_account_upgraded_facebook",
      "free_account_upgraded_google",
      "free_account_upgraded",
      "account_exists",
    ];
    if (successResults.includes(enrollmentResult)) {
      success = true;
    }
  } catch (err) {
    captureException(err);
  }

  trackEvent("OnCheckoutEnrollment", {
    result: enrollmentResult,
    success,
  });

  trackMmpEvent("mmpAccountCreated", {});

  if (!success) {
    if (enrollmentResult === "account_already_has_program") {
      throw new EnrollmentError(errorConstants.errorActiveSubscription);
    }
    if (enrollmentResult === "email_address_is_invalid") {
      throw new EnrollmentError(errorConstants.errorInvalidEmail);
    }
    if (enrollmentResult === "pending_deletion_request") {
      throw new EnrollmentError(errorConstants.errorPendingDeletionRequest);
    }
    throw new EnrollmentError();
  }

  updateSessionState("browser", {
    enrollmentResult,
    claimedByEmail,
    appDeepLink: deeplinkUrl as DeepLinkString,
  });

  // Update userData state to reflect the access code,
  // this will allow us to keep the same access code in growth
  store.dispatch(
    updateUserData({
      access_code: accessCode,
    })
  );

  return { enrollmentResult, claimedByEmail };
}

export async function enrollFreeProgramUser(surveyAnswers: SurveyAnswersState) {
  const state = getStore().getState();
  const { userData, serverContext } = state;
  const inAppAutoCookie = getInAppAutoCookie();
  const { freeOnboardingGlp1Status } = surveyAnswers;
  const isCurrentlyTakingGlp1 =
    freeOnboardingGlp1Status?.[0] === "yesCurrentlyTaking";

  const payload = {
    weightLossData: getWeightLossGoalData(),
    userProfileData: prepareFreeUserProfileData(surveyAnswers),
    country_code: getCountryCode(),
    language: getLanguage(),
    route: getRouteId(),
    ...(!inAppAutoCookie && {
      accessCode: userData.access_code || serverContext.access_code,
    }),
    isCurrentlyTakingGlp1,
  };
  if (!inAppAutoCookie) {
    return send("POST", API_ENDPOINT_V1_FREE_ON_WEB, payload);
  }
  return send("POST", API_ENDPOINT_V1_FREE, payload);
}

export async function enrollAppFreeOnboardingUser(
  surveyAnswers: SurveyAnswersState
) {
  const state = getStore().getState();
  const { userData, serverContext } = state;
  const { gender, heightCm, welcomeName, freeOnboardingGlp1Status } =
    surveyAnswers;
  const isCurrentlyTakingGlp1 =
    freeOnboardingGlp1Status?.[0] === "yesCurrentlyTaking";
  const inAppAutoCookie = getInAppAutoCookie();
  const { email: inAppEmail } = getInAppAutoCookie() ?? {};
  const { email: userDataEmail } = userData;
  const { email: contextEmail } = serverContext;

  const email = inAppEmail || userDataEmail || contextEmail;

  const payload = {
    weightLossData: getWeightLossGoalData(surveyAnswers),
    userProfileData: {
      email,
      name: welcomeName,
      age: surveyAnswers.ageYears,
      language: getLanguage(),
      heightInCm: heightCm,
      weightInKg: surveyAnswers.weightKg,
      gender: genderConstantsMapping[gender[0]],
    },
    country_code: getCountryCode,
    language: getLanguage,
    route: getRouteId(),
    accessCode: userData.access_code || serverContext.access_code,
    isCurrentlyTakingGlp1,
  };
  if (!inAppAutoCookie) {
    return send("POST", API_ENDPOINT_V1_FREE_ON_WEB, payload);
  }
  return send("POST", API_ENDPOINT_V1_FREE, payload);
}

export async function updateUserModelAndEnrollFreeUser(
  surveyAnswers: SurveyAnswersState
) {
  await Promise.all([
    publishBuyflowSurveyAnswersInUserModel(),
    isAppFreeOnboardingRoute()
      ? enrollAppFreeOnboardingUser(surveyAnswers)
      : enrollFreeProgramUser(surveyAnswers),
  ]);
  // Attach profile photo to user if it exists
  // This is a non-blocking side effect - enrollment should succeed even if photo upload fails
  const { profilePhoto, welcomeName, ageYears, gender } = surveyAnswers;
  if (profilePhoto) {
    try {
      await attachPhotoToUser(profilePhoto, {
        name: welcomeName,
        age: ageYears,
        gender: genderConstantsMapping[gender[0]],
      });
    } catch (error) {
      // Log the error but don't throw - photo upload failure shouldn't break enrollment
      captureException(error, "profile-photo-attachment-failed");
    }
  }
}
